"use client";

import { useState } from "react";
import { Search, Pencil, Trash2 } from "lucide-react";
import { VehicleSearchInputs } from "./VehicleSearchInputs";
import { VehicleOverlay } from "./SearchVehicleOverlay";
import { SelectedVehiclesOverlay } from "./SelectedVehiclesOverlay";
import { fetchVehicles } from "../../services/vehicleService";
import { Vehicle } from "../../types/vehicle.types";
import { showToast } from "../warnings/ToastPopup";
import { showPopup } from "../warnings/Popup";

interface VehicleFormProps {
  addVehicle: (vehicle: Vehicle) => void;
  removeVehicle: (id: number) => void;
  clearVehicles: () => void;
  selectedVehicles: Vehicle[];
  vehicleLoading: boolean;
  setVehicleLoading: (loading: boolean) => void;
}

function VehicleForm({ 
  addVehicle,
  removeVehicle,
  clearVehicles,
  vehicleLoading,
  setVehicleLoading, 
  selectedVehicles,
}: VehicleFormProps) {
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [editVehicles, setEditVehicles] = useState(false);

  const searchVehicles = async () => {
    try {
      setVehicleLoading(true);
      setShowSearchResults(true);
      const results = await fetchVehicles(make, model, year);

      if (!results) return;

      setVehicles(results);
    } catch {
      showPopup(
        "Unexpected Error",
        "An unexpected error has occured."
      )
    } finally {
      setVehicleLoading(false);
    }
  };

  const clearInput = (key: string) => {
    if (key === 'make') {
      setMake("");
    }
    if (key === 'model') {
      setModel("");
    }
    if (key === 'year') {
      setYear("");
    }
  }

  const handleSelectVehicle = (vehicle: Vehicle) => {
    addVehicle(vehicle);
    setShowSearchResults(false);
    clearInput("make");
    clearInput("model");
    clearInput("year");
    showToast(`Added ${vehicle.make} ${vehicle.model} (${vehicle.modelYear}) to vehicles.`);
  };

  const handleRemoveVehicle = (idx: number) => {
    removeVehicle(idx);
    showToast(`Removed ${selectedVehicles[idx-1].make} ${selectedVehicles[idx-1].model} (${selectedVehicles[idx-1].modelYear}) from vehicles.`);
  }

  const handleClearAllVehicles = () => {
    clearVehicles();
    showToast(`Removed All vehicles.`);
  }

  const vehiclesFlag = selectedVehicles.length>=1;

  return (
    <div className="vehicle-form-section relative">
      <VehicleSearchInputs
        make={make}
        setMake={setMake}
        model={model}
        setModel={setModel}
        year={year}
        setYear={setYear}
        onClear={clearInput}
      />

      <div className = "vehicle-input-buttons"> 
        <button 
          className="vehicle-search-button button" 
          onClick={searchVehicles}
          aria-label="Search button"
        >
          <Search size={20} className="lucide-icon" />
        </button>
        {vehiclesFlag && (
          <>
            <button 
              className="vehicle-search-button button"
              onClick={() => setEditVehicles(true)}
              aria-label="Edit vehicles"
            >
              <Pencil size={20} className="lucide-icon" />
            </button>
            <button 
              className="vehicle-search-button button"
              onClick={handleClearAllVehicles}
              aria-label="Clear vehicles"
            >
              <Trash2 size={20} className="lucide-icon" />
            </button>
          </>
        )}
      </div>

      {showSearchResults && (
        <VehicleOverlay
          vehicles={vehicles}
          onSelectVehicle={handleSelectVehicle}
          onClose={() => setShowSearchResults(false)}
          vehicleLoading={vehicleLoading}
        />
      )}

      {editVehicles && (
        <SelectedVehiclesOverlay
          selectedVehicles={selectedVehicles}
          onRemoveVehicle={handleRemoveVehicle}
          onClose={() => setEditVehicles(false)}
        />
      )}
    </div>
  );
}

export default VehicleForm;
